import { useState, useCallback, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useContractWrite, usePrepareContractWrite, useContractRead, useAccount, useNetwork } from 'wagmi';
import { MULTISENDER_ADDRESSES } from '@/hooks/useMultisender';
import { useToast } from '@/components/ui/use-toast';
import { parseEther, formatEther, isAddress } from 'viem';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { HelpCircle, ExternalLink, Copy, CheckCircle } from 'lucide-react';

// Multisender contract ABI for admin functions
const MULTISENDER_ADMIN_ABI = [
  {
    inputs: [],
    name: 'feePerRecipient',
    outputs: [{ name: '', type: 'uint256' }],
    stateMutability: 'view',
    type: 'function'
  },
  {
    inputs: [],
    name: 'feeReceiver',
    outputs: [{ name: '', type: 'address' }],
    stateMutability: 'view',
    type: 'function'
  },
  {
    inputs: [],
    name: 'owner',
    outputs: [{ name: '', type: 'address' }],
    stateMutability: 'view',
    type: 'function'
  },
  {
    inputs: [{ name: 'newFeePerRecipient', type: 'uint256' }],
    name: 'setFeePerRecipient',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function'
  },
  {
    inputs: [{ name: 'newFeeReceiver', type: 'address' }],
    name: 'setFeeReceiver',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function'
  },
  {
    inputs: [{ name: 'newOwner', type: 'address' }],
    name: 'transferOwnership',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function'
  }
] as const;

export function MultisenderContractManagement() {
  const { toast } = useToast();
  const { address } = useAccount();
  const { chain } = useNetwork();
  const [newFee, setNewFee] = useState('');
  const [newFeeReceiver, setNewFeeReceiver] = useState('');
  const [newOwnerAddress, setNewOwnerAddress] = useState('');
  const [isOwnerVerified, setIsOwnerVerified] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const chainId = chain?.id || 97; // Default to BSC testnet
  const multisenderAddress = MULTISENDER_ADDRESSES[chainId];

  // Get native currency symbol based on chain
  const getNativeCurrencySymbol = () => {
    return chain?.nativeCurrency?.symbol || 'BNB';
  };

  // Get current fee per recipient
  const { data: currentFee, refetch: refetchFee } = useContractRead({
    address: multisenderAddress as `0x${string}`,
    abi: MULTISENDER_ADMIN_ABI,
    functionName: 'feePerRecipient',
    watch: true,
    enabled: Boolean(multisenderAddress && multisenderAddress !== '0x0000000000000000000000000000000000000000'),
  });

  // Get current fee receiver
  const { data: currentFeeReceiver, refetch: refetchReceiver } = useContractRead({
    address: multisenderAddress as `0x${string}`,
    abi: MULTISENDER_ADMIN_ABI,
    functionName: 'feeReceiver',
    watch: true,
    enabled: Boolean(multisenderAddress && multisenderAddress !== '0x0000000000000000000000000000000000000000'),
  });

  // Get contract owner
  const { data: contractOwner, isSuccess: ownerFetchSuccess } = useContractRead({
    address: multisenderAddress as `0x${string}`,
    abi: MULTISENDER_ADMIN_ABI,
    functionName: 'owner',
    watch: true,
    enabled: Boolean(multisenderAddress && multisenderAddress !== '0x0000000000000000000000000000000000000000'),
  });

  // Verify ownership
  useEffect(() => {
    if (ownerFetchSuccess && contractOwner && address) {
      const isOwner = (contractOwner as string).toLowerCase() === address.toLowerCase();
      setIsOwnerVerified(isOwner);
      console.log('Ownership verification:', { contractOwner, address, isOwner });
    }
  }, [contractOwner, address, ownerFetchSuccess]);

  // Set Fee Per Recipient
  const { config: setFeeConfig } = usePrepareContractWrite({
    address: multisenderAddress as `0x${string}`,
    abi: MULTISENDER_ADMIN_ABI,
    functionName: 'setFeePerRecipient',
    args: newFee ? [parseEther(newFee)] : undefined,
    enabled: Boolean(newFee && isOwnerVerified && multisenderAddress),
  });

  const { write: setFeeWrite, isLoading: isSettingFee } = useContractWrite({
    ...setFeeConfig,
    onSuccess: () => {
      toast({
        title: "Fee Updated",
        description: `Fee per recipient set to ${newFee} ${getNativeCurrencySymbol()}`,
      });
      setNewFee('');
      refetchFee();
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Set Fee Receiver
  const { config: setReceiverConfig } = usePrepareContractWrite({
    address: multisenderAddress as `0x${string}`,
    abi: MULTISENDER_ADMIN_ABI,
    functionName: 'setFeeReceiver',
    args: newFeeReceiver && isAddress(newFeeReceiver) ? [newFeeReceiver as `0x${string}`] : undefined,
    enabled: Boolean(newFeeReceiver && isAddress(newFeeReceiver) && isOwnerVerified && multisenderAddress),
  });

  const { write: setReceiverWrite, isLoading: isSettingReceiver } = useContractWrite({
    ...setReceiverConfig,
    onSuccess: () => {
      toast({
        title: "Fee Receiver Updated",
        description: `Fee receiver set to ${newFeeReceiver}`,
      });
      setNewFeeReceiver('');
      refetchReceiver();
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Transfer Ownership
  const { config: transferOwnershipConfig } = usePrepareContractWrite({
    address: multisenderAddress as `0x${string}`,
    abi: MULTISENDER_ADMIN_ABI,
    functionName: 'transferOwnership',
    args: newOwnerAddress && isAddress(newOwnerAddress) ? [newOwnerAddress as `0x${string}`] : undefined,
    enabled: Boolean(newOwnerAddress && isAddress(newOwnerAddress) && isOwnerVerified && multisenderAddress),
  });

  const { write: transferOwnershipWrite, isLoading: isTransferringOwnership } = useContractWrite({
    ...transferOwnershipConfig,
    onSuccess: () => {
      toast({
        title: "Ownership Transferred",
        description: `Contract ownership transferred to ${newOwnerAddress}`,
      });
      setNewOwnerAddress('');
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const copyToClipboard = useCallback((text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  }, []);

  const getExplorerUrl = (address: string) => {
    if (chainId === 97) return `https://testnet.bscscan.com/address/${address}`;
    if (chainId === 56) return `https://bscscan.com/address/${address}`;
    return `https://etherscan.io/address/${address}`;
  };

  if (!multisenderAddress || multisenderAddress === '0x0000000000000000000000000000000000000000') {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Multisender Contract Management</CardTitle>
          <CardDescription>Manage platform fees and contract settings</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <p className="text-muted-foreground">
              Multisender contract not deployed on {chain?.name || 'this network'} yet.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Multisender Contract Management</CardTitle>
        <CardDescription>Manage platform fees, receiver address, and contract ownership</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Network Status */}
        <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
          <div>
            <p className="text-sm font-medium">Network Status</p>
            <p className="text-xs text-muted-foreground">Chain ID: {chainId}</p>
          </div>
          <div className="text-right">
            <p className="text-sm font-medium">{chainId === 97 ? 'BSC Testnet' : chain?.name || 'Unknown'}</p>
            <p className="text-xs text-muted-foreground">Owner Status: {isOwnerVerified ? 'Owner' : 'Not Owner'}</p>
          </div>
        </div>

        {/* Contract Addresses */}
        <div className="space-y-3">
          <h3 className="text-sm font-semibold">Contract Addresses</h3>
          
          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">Multisender Contract</Label>
            <div className="flex gap-2">
              <Input
                value={multisenderAddress}
                readOnly
                className="bg-muted font-mono text-xs"
              />
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => copyToClipboard(multisenderAddress, 'multisender')}
                    >
                      {copiedField === 'multisender' ? <CheckCircle className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Copy address</TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => window.open(getExplorerUrl(multisenderAddress), '_blank')}
                    >
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>View on explorer</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
        </div>

        {/* Platform Fee */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold">Platform Fee</h3>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <HelpCircle className="h-4 w-4 text-muted-foreground" />
                </TooltipTrigger>
                <TooltipContent>
                  <p className="max-w-xs">Fee charged per recipient in {getNativeCurrencySymbol()}. Maximum 1 {getNativeCurrencySymbol()} per recipient.</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>

          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">Current Fee</Label>
            <div className="flex gap-2 items-center">
              <Input
                value={currentFee ? `${formatEther(currentFee as bigint)} ${getNativeCurrencySymbol()}` : 'Loading...'}
                readOnly
                className="bg-muted font-mono"
              />
              <Button
                variant="outline"
                size="icon"
                onClick={() => refetchFee()}
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </Button>
            </div>
          </div>

          <div className="space-y-2">
            <Label>New Fee ({getNativeCurrencySymbol()})</Label>
            <div className="flex gap-2">
              <Input
                type="number"
                step="0.0001"
                placeholder="Enter new fee (e.g., 0.0025)"
                value={newFee}
                onChange={(e) => setNewFee(e.target.value)}
                disabled={!isOwnerVerified}
              />
              <Button
                onClick={() => setFeeWrite?.()}
                disabled={!setFeeWrite || isSettingFee || !isOwnerVerified}
              >
                {isSettingFee ? 'Updating...' : 'Update'}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Make sure the new fee is a valid {getNativeCurrencySymbol()} amount (e.g., 0.0025 for 0.0025 {getNativeCurrencySymbol()} per recipient)
            </p>
          </div>
        </div>

        {/* Fee Receiver */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold">Fee Receiver</h3>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <HelpCircle className="h-4 w-4 text-muted-foreground" />
                </TooltipTrigger>
                <TooltipContent>
                  <p className="max-w-xs">Address that receives collected platform fees</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>

          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">Current Receiver</Label>
            <div className="flex gap-2">
              <Input
                value={currentFeeReceiver as string || 'Loading...'}
                readOnly
                className="bg-muted font-mono text-xs"
              />
              {currentFeeReceiver && (
                <>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => copyToClipboard(currentFeeReceiver as string, 'receiver')}
                  >
                    {copiedField === 'receiver' ? <CheckCircle className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => window.open(getExplorerUrl(currentFeeReceiver as string), '_blank')}
                  >
                    <ExternalLink className="h-4 w-4" />
                  </Button>
                </>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label>New Receiver Address</Label>
            <div className="flex gap-2">
              <Input
                placeholder="Enter new receiver address"
                value={newFeeReceiver}
                onChange={(e) => setNewFeeReceiver(e.target.value)}
                className={newFeeReceiver && !isAddress(newFeeReceiver) ? 'border-red-500' : ''}
                disabled={!isOwnerVerified}
              />
              <Button
                onClick={() => setReceiverWrite?.()}
                disabled={!setReceiverWrite || isSettingReceiver || !isOwnerVerified}
              >
                {isSettingReceiver ? 'Updating...' : 'Update'}
              </Button>
            </div>
            {newFeeReceiver && !isAddress(newFeeReceiver) && (
              <p className="text-xs text-destructive">Invalid address format</p>
            )}
          </div>
        </div>

        {/* Ownership Management */}
        <div className="space-y-3 border-t pt-4">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold">Ownership Management</h3>
          </div>

          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">Current Owner</Label>
            <div className="flex gap-2">
              <Input
                value={contractOwner as string || 'Loading...'}
                readOnly
                className="bg-muted font-mono text-xs"
              />
              {contractOwner && (
                <>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => copyToClipboard(contractOwner as string, 'owner')}
                  >
                    {copiedField === 'owner' ? <CheckCircle className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => window.open(getExplorerUrl(contractOwner as string), '_blank')}
                  >
                    <ExternalLink className="h-4 w-4" />
                  </Button>
                </>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label>Transfer Ownership</Label>
            <div className="flex gap-2">
              <Input
                placeholder="Enter new owner address"
                value={newOwnerAddress}
                onChange={(e) => setNewOwnerAddress(e.target.value)}
                className={newOwnerAddress && !isAddress(newOwnerAddress) ? 'border-red-500' : ''}
                disabled={!isOwnerVerified}
              />
              <Button
                onClick={() => transferOwnershipWrite?.()}
                disabled={!transferOwnershipWrite || isTransferringOwnership || !isOwnerVerified}
                variant="destructive"
              >
                {isTransferringOwnership ? 'Transferring...' : 'Transfer'}
              </Button>
            </div>
            {newOwnerAddress && !isAddress(newOwnerAddress) && (
              <p className="text-xs text-destructive">Invalid address format</p>
            )}
            <p className="text-xs text-yellow-600 dark:text-yellow-500">
              ⚠️ Warning: Transferring ownership will remove your ability to manage this contract. Make sure the new owner address is correct.
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
